import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useCategories } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import NotificationBell from '../../components/common/NotificationBell';
import UserAvatar from '../../components/common/UserAvatar';
import CustomerBottomNav from '../../components/common/CustomerBottomNav';
import {
  ArrowLeft, Search, MapPin, Star, Clock, CheckCircle, Circle, XCircle,
  Wrench, Droplets, Zap, Wind, Sparkles, Hammer, Tv, PaintBucket,
  Package, Car, Bug, Leaf, Truck, Monitor, Flame, Shield, Scissors, Heart, Brush, Pipette, Plug, Cog,
  ChevronLeft, ChevronRight, ArrowUpDown, Award, Eye, Briefcase
} from 'lucide-react';
import UnifiedMap from '../../components/common/UnifiedMap';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  'Droplets': Droplets, 'Zap': Zap, 'Wind': Wind, 'Sparkles': Sparkles,
  'Hammer': Hammer, 'Tv': Tv, 'PaintBucket': PaintBucket, 'Wrench': Wrench,
  'Package': Package, 'Car': Car, 'Bug': Bug, 'Leaf': Leaf, 'Truck': Truck,
  'Monitor': Monitor, 'Flame': Flame, 'Shield': Shield, 'Scissors': Scissors,
  'Heart': Heart, 'Brush': Brush, 'Pipette': Pipette, 'Plug': Plug, 'Cog': Cog,
};

const ITEMS_PER_PAGE = 10;

export default function ServiceSearch() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { categories } = useCategories();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('all');
  const [allServices, setAllServices] = useState([]);
  const [userLocation, setUserLocation] = useState({ lat: 14.5995, lng: 120.9842 });
  const [isLoading, setIsLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [sortBy, setSortBy] = useState('relevance');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [selectedProviderName, setSelectedProviderName] = useState(null);
  const [showServiceDetails, setShowServiceDetails] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [providerCertifications, setProviderCertifications] = useState([]);
  const [loadingCertifications, setLoadingCertifications] = useState(false);

  // Fetch provider certifications
  const fetchProviderCertifications = async (providerId) => {
    setLoadingCertifications(true);
    try {
      const res = await axios.get(`${API}/providers/${providerId}/certifications`);
      setProviderCertifications(res.data || []);
    } catch (e) {
      setProviderCertifications([]);
    } finally {
      setLoadingCertifications(false);
    }
  };

  // Open service details popup
  const openServiceDetails = (service) => {
    setSelectedService(service);
    setShowServiceDetails(true);
    if (service.provider_id) {
      fetchProviderCertifications(service.provider_id);
    }
  };

  // Detect user location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, []);

  // Search function - called directly, not via useEffect
  const doSearch = async (catId, online, keyword, sort = sortBy, providerId = null) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (catId && catId !== 'all') {
        params.append('category_id', catId);
      }
      if (keyword && keyword.trim()) {
        params.append('keyword', keyword.trim());
      }
      params.append('latitude', userLocation.lat.toString());
      params.append('longitude', userLocation.lng.toString());
      params.append('radius_km', '100');
      if (online) {
        params.append('online_only', 'true');
      }
      if (sort && sort !== 'relevance') {
        params.append('sort_by', sort);
      }
      // Add provider filter if specified
      if (providerId) {
        params.append('provider_id', providerId);
      }
      params.append('limit', '200');
      
      const res = await axios.get(`${API}/services/search?${params.toString()}`);
      setAllServices(res.data || []);
      setCurrentPage(1);
    } catch (e) {
      console.error('Search failed:', e);
      setAllServices([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial search on mount - check for URL params
  useEffect(() => {
    const catId = searchParams.get('category');
    const providerId = searchParams.get('provider_id');
    const providerName = searchParams.get('provider_name');
    
    if (providerId) {
      setSelectedProviderId(providerId);
      setSelectedProviderName(providerName ? decodeURIComponent(providerName) : null);
    }
    
    if (catId) {
      setSelectedCategoryId(catId);
      doSearch(catId, false, '', sortBy, providerId);
    } else {
      doSearch('all', false, '', sortBy, providerId);
    }
  }, []);

  // Handle URL param changes (when navigating with category)
  useEffect(() => {
    const catId = searchParams.get('category');
    const providerId = searchParams.get('provider_id');
    
    if (catId && catId !== selectedCategoryId) {
      setSelectedCategoryId(catId);
      doSearch(catId, onlineOnly, searchTerm, sortBy, providerId || selectedProviderId);
    }
  }, [searchParams]);

  // Handle category change
  const handleCategoryChange = (value) => {
    setSelectedCategoryId(value);
    doSearch(value, onlineOnly, searchTerm, sortBy, selectedProviderId);
  };

  // Handle online filter change
  const handleOnlineFilterChange = () => {
    const newOnlineOnly = !onlineOnly;
    setOnlineOnly(newOnlineOnly);
    doSearch(selectedCategoryId, newOnlineOnly, searchTerm, sortBy, selectedProviderId);
  };

  // Handle search button click
  const handleSearch = () => {
    doSearch(selectedCategoryId, onlineOnly, searchTerm, sortBy, selectedProviderId);
  };

  // Handle sort change
  const handleSortChange = (value) => {
    setSortBy(value);
    doSearch(selectedCategoryId, onlineOnly, searchTerm, value, selectedProviderId);
  };
  
  // Clear provider filter
  const clearProviderFilter = () => {
    setSelectedProviderId(null);
    setSelectedProviderName(null);
    navigate('/service-search', { replace: true });
    doSearch(selectedCategoryId, onlineOnly, searchTerm, sortBy, null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleBookService = (service) => {
    navigate(`/book/${service.id}?provider=${service.provider_id}`);
  };

  // Pagination
  const totalPages = Math.ceil(allServices.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedServices = allServices.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Get unique provider locations for map markers
  const providerMarkers = allServices
    .filter((s, i, arr) => arr.findIndex(x => x.provider_id === s.provider_id) === i)
    .filter(s => s.provider_latitude && s.provider_longitude)
    .map(s => ({
      lat: s.provider_latitude,
      lng: s.provider_longitude,
      color: 'blue',
      label: s.provider_name
    }));

  return (
    <div className="min-h-screen bg-stone-50 pb-20" data-testid="search-page">
      {/* Header */}
      <header className="bg-slate-900 pt-4 pb-4 px-4">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate('/home')} className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-white font-bold text-lg font-[Poppins] flex-1">Find Services</h1>
          <NotificationBell />
        </div>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search services by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-12 pl-12 pr-20 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 rounded-xl"
            data-testid="search-input"
          />
          <Button 
            size="sm" 
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-amber-500 hover:bg-amber-600 text-slate-900"
            onClick={handleSearch}
            data-testid="search-btn"
          >
            Search
          </Button>
        </div>
      </header>

      <main className="px-4 py-4 space-y-4 max-w-lg mx-auto">
        {/* Provider filter banner */}
        {selectedProviderId && (
          <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl px-4 py-3" data-testid="provider-filter-banner">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
              <span className="text-sm text-blue-900">
                Showing services from: <span className="font-semibold">{selectedProviderName || 'Selected Provider'}</span>
              </span>
            </div>
            <button 
              onClick={clearProviderFilter} 
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
              data-testid="clear-provider-filter"
            >
              <XCircle className="w-4 h-4" />
              Clear
            </button>
          </div>
        )}
        
        {/* Filters Row */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Category Dropdown */}
          <Select value={selectedCategoryId} onValueChange={handleCategoryChange}>
            <SelectTrigger className="w-[180px] h-9 bg-white" data-testid="category-dropdown">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Online Only Toggle */}
          <button
            onClick={handleOnlineFilterChange}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium h-9 transition-colors ${
              onlineOnly ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-white text-slate-600 border'
            }`}
            data-testid="filter-online"
          >
            {onlineOnly ? <CheckCircle className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
            Online Now
          </button>

          {/* Sort Dropdown */}
          <Select value={sortBy} onValueChange={handleSortChange}>
            <SelectTrigger className="w-[150px] h-9 bg-white" data-testid="sort-dropdown">
              <ArrowUpDown className="w-3.5 h-3.5 mr-1 text-slate-400" />
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="relevance">Relevance</SelectItem>
              <SelectItem value="price_low">Price: Low to High</SelectItem>
              <SelectItem value="price_high">Price: High to Low</SelectItem>
              <SelectItem value="rating">Highest Rating</SelectItem>
              <SelectItem value="distance">Nearest First</SelectItem>
              <SelectItem value="most_jobs">Most Experienced</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Results Count & Map Toggle */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600" data-testid="results-count">
            {isLoading ? 'Searching...' : allServices.length > 0 ? `${allServices.length} service${allServices.length !== 1 ? 's' : ''} found` : 'No services found'}
          </p>
          {allServices.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowMap(!showMap)}
              data-testid="toggle-map-btn"
            >
              <MapPin className="w-3.5 h-3.5 mr-1" />
              {showMap ? 'Hide Map' : 'Show Map'}
            </Button>
          )}
        </div>

        {/* Map */}
        {showMap && allServices.length > 0 && (
          <div className="rounded-xl overflow-hidden shadow-sm border">
            <UnifiedMap
              center={[userLocation.lat, userLocation.lng]}
              zoom={11}
              height="250px"
              markers={[
                { lat: userLocation.lat, lng: userLocation.lng, color: 'red', label: 'You' },
                ...providerMarkers
              ]}
            />
            <div className="bg-white p-2 text-xs text-slate-500 flex items-center gap-4">
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded-full"></span> Your Location</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 rounded-full"></span> Providers ({providerMarkers.length})</span>
            </div>
          </div>
        )}

        {/* Service Cards */}
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          </div>
        ) : paginatedServices.length > 0 ? (
          <div className="space-y-3">
            {paginatedServices.map(svc => {
              const CatIcon = iconMap[svc.category_icon] || Wrench;
              return (
                <div 
                  key={svc.id} 
                  className="bg-white rounded-2xl p-4 shadow-sm border border-stone-100 hover:shadow-md transition-shadow"
                  data-testid={`service-${svc.id}`}
                >
                  <div className="flex gap-3">
                    {/* Service Photo or Category Icon */}
                    <div className="w-20 h-20 rounded-xl bg-stone-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {svc.photo_url ? (
                        <img src={svc.photo_url} alt={svc.name} className="w-full h-full object-cover" />
                      ) : (
                        <CatIcon className="w-8 h-8 text-slate-400" />
                      )}
                    </div>
                    
                    {/* Service Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-semibold text-slate-900 text-sm truncate">{svc.name}</h3>
                          <p className="text-xs text-slate-500 truncate">{svc.category_name}</p>
                        </div>
                        <span className="text-amber-600 font-bold text-sm whitespace-nowrap">
                          ₱{svc.price?.toLocaleString()}
                        </span>
                      </div>
                      
                      {svc.description && (
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{svc.description}</p>
                      )}
                      
                      {/* Provider Info */}
                      <div className="flex items-center gap-2 mt-2">
                        <UserAvatar 
                          src={svc.provider_image} 
                          name={svc.provider_name} 
                          role="provider"
                          size="xs"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-700 truncate font-medium">{svc.provider_name}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span className="flex items-center gap-0.5">
                              <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                              {svc.provider_rating?.toFixed(1) || '0.0'}
                            </span>
                            <span>({svc.provider_total_jobs || 0} jobs)</span>
                            {svc.distance_km && (
                              <span className="flex items-center gap-0.5">
                                <MapPin className="w-3 h-3" />
                                {svc.distance_km}km
                              </span>
                            )}
                          </div>
                        </div>
                        {svc.provider_is_online && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Online</span>
                        )}
                      </div>
                      
                      {/* Duration & Buttons */}
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {svc.duration_hours ? `~${svc.duration_hours}h` : 'Varies'}
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-slate-600"
                            onClick={() => openServiceDetails(svc)}
                            data-testid={`view-service-${svc.id}`}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" />
                            Details
                          </Button>
                          <Button
                            size="sm"
                            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                            onClick={() => handleBookService(svc)}
                            data-testid={`book-service-${svc.id}`}
                          >
                            Book Now
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <MapPin className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-slate-700 font-medium mb-1">No services found</h3>
            {selectedProviderId ? (
              <>
                <p className="text-slate-400 text-sm mb-4">
                  {selectedProviderName || 'This provider'} hasn't added any services yet.
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={clearProviderFilter}
                >
                  Browse All Services
                </Button>
              </>
            ) : (
              <>
                <p className="text-slate-400 text-sm mb-4">Try selecting a different category or clear filters</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => { 
                    setSelectedCategoryId('all'); 
                    setOnlineOnly(false); 
                    doSearch('all', false, '');
                  }}
                >
                  Clear Filters
                </Button>
              </>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="h-9 px-3"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                .map((page, index, arr) => {
                  const showEllipsis = index > 0 && page - arr[index - 1] > 1;
                  return (
                    <div key={page} className="flex items-center">
                      {showEllipsis && <span className="px-2 text-slate-400">...</span>}
                      <button
                        onClick={() => goToPage(page)}
                        className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-slate-900 text-white'
                            : 'bg-white text-slate-700 hover:bg-stone-100 border'
                        }`}
                      >
                        {page}
                      </button>
                    </div>
                  );
                })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="h-9 px-3"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
        
        {/* Page info */}
        {totalPages > 1 && (
          <p className="text-center text-xs text-slate-400">
            Showing {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, allServices.length)} of {allServices.length} services
          </p>
        )}
      </main>

      <CustomerBottomNav />

      {/* Service Details Popup */}
      <Dialog open={showServiceDetails} onOpenChange={setShowServiceDetails}>
        <DialogContent className="max-w-md mx-auto max-h-[90vh] overflow-y-auto" data-testid="service-details-dialog">
          <DialogHeader>
            <DialogTitle>Service Details</DialogTitle>
          </DialogHeader>
          
          {selectedService && (
            <div className="space-y-4 py-2">
              {/* Service Info */}
              <div className="bg-stone-50 rounded-xl p-4">
                <div className="flex gap-3">
                  <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center overflow-hidden flex-shrink-0 border border-stone-200">
                    {selectedService.photo_url ? (
                      <img src={selectedService.photo_url} alt={selectedService.name} className="w-full h-full object-cover" />
                    ) : (
                      <Wrench className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{selectedService.name}</h3>
                    <p className="text-xs text-slate-500">{selectedService.category_name}</p>
                    <p className="text-amber-600 font-bold mt-1">₱{selectedService.price?.toLocaleString()}</p>
                  </div>
                </div>
                {selectedService.description && (
                  <p className="text-sm text-slate-600 mt-3">{selectedService.description}</p>
                )}
                <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {selectedService.duration_hours ? `~${selectedService.duration_hours} hour(s)` : 'Duration varies'}
                  </span>
                </div>
              </div>

              {/* Provider Info */}
              <div className="bg-white rounded-xl p-4 border border-stone-200">
                <h4 className="font-semibold text-slate-900 text-sm mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-slate-400" />
                  Service Provider
                </h4>
                <div className="flex items-start gap-3">
                  <UserAvatar 
                    src={selectedService.provider_image} 
                    name={selectedService.provider_name} 
                    role="provider"
                    size="lg"
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">{selectedService.provider_name}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="flex items-center gap-1 text-sm">
                        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                        <span className="font-medium">{selectedService.provider_rating?.toFixed(1) || '0.0'}</span>
                      </span>
                      <span className="text-sm text-slate-500">
                        {selectedService.provider_total_jobs || 0} jobs completed
                      </span>
                    </div>
                    {selectedService.provider_is_online && (
                      <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                        Online Now
                      </span>
                    )}
                    {selectedService.provider_bio && (
                      <p className="text-sm text-slate-600 mt-2">{selectedService.provider_bio}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Certifications */}
              <div className="bg-white rounded-xl p-4 border border-stone-200">
                <h4 className="font-semibold text-slate-900 text-sm mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4 text-amber-500" />
                  Certifications & Credentials
                </h4>
                {loadingCertifications ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-400"></div>
                  </div>
                ) : providerCertifications.length > 0 ? (
                  <div className="space-y-2">
                    {providerCertifications.map((cert, idx) => (
                      <div key={cert.id || idx} className="flex items-center gap-2 p-2 bg-stone-50 rounded-lg">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{cert.title}</p>
                          {cert.issuer && (
                            <p className="text-xs text-slate-500">{cert.issuer} {cert.year_obtained && `• ${cert.year_obtained}`}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-2">No certifications added yet</p>
                )}
              </div>

              {/* Book Button */}
              <Button
                className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                onClick={() => {
                  setShowServiceDetails(false);
                  handleBookService(selectedService);
                }}
                data-testid="book-from-details-btn"
              >
                Book This Service
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
